import { useTheme } from '@mui/material/styles'
import type { ApexOptions } from 'apexcharts'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import appColors from 'themes/colors'
import { ThemeMode } from 'types/config'

interface CompensationChartProps {
  salary?: number
  variable?: number
  customSeries?: number[]
  customLabels?: string[]
  currency?: string
  currencyDecimals?: number
  height?: string | number
  legendPosition?: 'top' | 'bottom' | 'left' | 'right'
  animated?: boolean
  verticalLegend?: boolean
}

const CompensationChart: FC<CompensationChartProps> = ({
  salary,
  variable,
  customSeries,
  customLabels,
  currency = 'USD',
  currencyDecimals = 0,
  height = 300,
  legendPosition = 'bottom',
  animated = true,
  verticalLegend = false,
}: CompensationChartProps) => {
  const theme = useTheme()
  const [isReady, setIsReady] = useState(false)

  // Small delay to ensure DOM is ready before rendering the chart
  useEffect(() => {
    setIsReady(false)
    const timer = setTimeout(() => setIsReady(true), 100)
    return () => clearTimeout(timer)
  }, [customSeries, customLabels, theme.palette.mode])

  const formatCurrency = (value: number, cur: string, dec: number) => {
    if (typeof value !== 'number' || isNaN(value)) return `${cur} 0`

    try {
      return new Intl.NumberFormat('default', {
        style: 'currency',
        currency: cur,
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      }).format(value)
    } catch (error) {
      console.error('Error formatting currency:', error)
      return `${cur} ${value.toFixed(dec)}`
    }
  }

  // Generate enough colors for all series using the same color scheme as VariableCompensationChart
  const seriesCount = customSeries?.length || 2
  const chartColors =
    theme.palette.mode === ThemeMode.DARK
      ? [
          appColors.turquoise, // #1DD3B0 - Primary
          appColors.caribbeanBlue, // #087575 - Primary
          appColors.charcoalBlue, // #193C4D - Accent
          appColors.pear, // #D1E231 - Accent
          appColors.kellyGreen, // #32AE2C - Accent
          appColors.crayolaRed, // #F00553 - Accent
          '#6B4E9C', // Purple - Extrapolated
          '#E67E22', // Orange - Extrapolated
          '#3498DB', // Sky Blue - Extrapolated
          '#8E44AD', // Deep Purple - Extrapolated
          '#F39C12', // Golden Yellow - Extrapolated
          '#2ECC71', // Emerald Green - Extrapolated
        ].slice(0, seriesCount)
      : [
          appColors.turquoise, // #1DD3B0 - Primary
          appColors.caribbeanBlue, // #087575 - Primary
          '#3498DB', // Sky Blue - Extrapolated (moved to 3rd)
          '#2ECC71', // Emerald Green - Extrapolated (moved to 4th)
          appColors.pear, // #D1E231 - Accent (5th)
          appColors.charcoalBlue, // #193C4D - Accent (6th)
          appColors.kellyGreen, // #32AE2C - Accent
          appColors.crayolaRed, // #F00553 - Accent
          '#6B4E9C', // Purple - Extrapolated
          '#E67E22', // Orange - Extrapolated
          '#8E44AD', // Deep Purple - Extrapolated
          '#F39C12', // Golden Yellow - Extrapolated
        ].slice(0, seriesCount)

  // Calculate responsive font sizes based on height
  const numericHeight = typeof height === 'string' ? parseInt(height) || 300 : height
  const baseFontSize = Math.max(10, Math.min(16, numericHeight / 16)) // Scale between 10px and 16px
  const totalFontSize = Math.max(12, Math.min(18, numericHeight / 14)) // Slightly larger for total
  const legendFontSize = Math.max(10, Math.min(14, numericHeight / 16)) // Smaller for legend

  const chartOptions: ApexOptions = {
    chart: {
      type: 'donut',
      animations: {
        enabled: animated,
      },
    },
    colors: chartColors,
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: false,
    },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              color: theme.palette.text.primary,
              fontSize: `${totalFontSize}px`,
              fontWeight: 'bold',
              fontFamily: theme.typography.fontFamily,
              showAlways: true,
              show: true,
              formatter(w) {
                try {
                  if (!w?.globals?.seriesTotals?.length) return formatCurrency(0, currency, currencyDecimals)
                  const total = w.globals.seriesTotals.reduce((a: number, b: number) => a + (isNaN(b) ? 0 : b), 0)
                  return formatCurrency(total, currency, currencyDecimals)
                } catch (error) {
                  console.error('Error in total formatter:', error)
                  return formatCurrency(0, currency, currencyDecimals)
                }
              },
            },
            value: {
              color: theme.palette.text.primary,
              fontSize: `${baseFontSize}px`,
              fontFamily: theme.typography.fontFamily,
              show: true,
              offsetY: -5,
            },
            name: {
              color: theme.palette.text.primary,
              fontSize: `${baseFontSize}px`,
              fontFamily: theme.typography.fontFamily,
              show: true,
            },
          },
        },
      },
    },
    legend: {
      position: legendPosition,
      horizontalAlign: 'left',
      fontSize: `${legendFontSize}px`,
      fontFamily: theme.typography.fontFamily,
      itemMargin: {
        horizontal: 4,
        vertical: 2,
      },
      markers: {
        size: 4,
        offsetX: -2,
      },
      labels: {
        colors: theme.palette.text.primary,
        useSeriesColors: false,
      },
      formatter(val, opts) {
        try {
          const seriesValue = opts?.w?.globals?.series?.[opts.seriesIndex]
          if (typeof seriesValue === 'number' && !isNaN(seriesValue)) {
            return `${val} - ${formatCurrency(seriesValue, currency, currencyDecimals)}`
          }
          return `${val} - N/A`
        } catch (error) {
          console.error('Error in legend formatter:', error)
          return `${val} - N/A`
        }
      },
    },
    tooltip: {
      enabled: true,
      fixed: {
        enabled: true,
        position: 'topRight',
        offsetX: 0,
        offsetY: 0,
      },
      y: {
        formatter(val) {
          try {
            if (typeof val === 'number' && !isNaN(val)) {
              return formatCurrency(val, currency, currencyDecimals)
            }
            return 'N/A'
          } catch (error) {
            console.error('Error in tooltip formatter:', error)
            return 'N/A'
          }
        },
      },
      theme: theme.palette.mode === ThemeMode.DARK ? 'dark' : 'light',
    },
    labels: customLabels || ['Base', 'Variable'],
  }

  const series: ApexNonAxisChartSeries = customSeries || [salary || 0, variable || 0]

  return (
    <>
      {isReady ? (
        <div
          className={verticalLegend ? 'vertical-legend-chart' : ''}
          style={
            verticalLegend
              ? {
                  width: '100%',
                }
              : undefined
          }
        >
          <ReactApexChart
            key={`apex-salary-chart-${theme.palette.mode}-${series.join('-')}`}
            options={chartOptions}
            series={series}
            type="donut"
            height={height}
            width="100%"
          />
          {verticalLegend && (
            <style>
              {`
                .vertical-legend-chart .apexcharts-legend {
                  display: flex !important;
                  flex-direction: column !important;
                  align-items: flex-start !important;
                  overflow: visible !important;
                  max-height: none !important;
                }
                .vertical-legend-chart .apexcharts-legend-series {
                  margin: 2px 0 !important;
                }
              `}
            </style>
          )}
        </div>
      ) : (
        <div>Loading chart...</div>
      )}
    </>
  )
}

export default CompensationChart
